import {
    getBusesRemainingOnLimitedSubscription,
    Subscription
} from "types/subscriptionType";

/** We don't know yet which of the states below applies. */
export const UNLOCK_STATE_LOADING = "loading";
/** The team has already unlocked this bus, so opening it costs nothing. */
export const UNLOCK_STATE_ALREADY_UNLOCKED = "alreadyUnlocked";
/** Unlock silently on select, the way the app has always behaved. */
export const UNLOCK_STATE_UNLOCK_ON_SELECT = "unlockOnSelect";
/** Unlocking would spend one of a limited number of buses: ask first. */
export const UNLOCK_STATE_NEEDS_CONFIRMATION = "needsConfirmation";
/**
 * We can't tell whether unlocking would spend a bus -- the fresh status check
 * failed and the login-time count can't answer for it -- so spend nothing.
 */
export const UNLOCK_STATE_UNAVAILABLE = "unavailable";

export type BusUnlockState =
    | { readonly kind: typeof UNLOCK_STATE_LOADING }
    | { readonly kind: typeof UNLOCK_STATE_ALREADY_UNLOCKED }
    | { readonly kind: typeof UNLOCK_STATE_UNLOCK_ON_SELECT }
    | { readonly kind: typeof UNLOCK_STATE_UNAVAILABLE }
    | {
          readonly kind: typeof UNLOCK_STATE_NEEDS_CONFIRMATION;
          /** Buses left on the subscription *before* this bus is unlocked. */
          readonly busesRemaining: number;
          readonly numBuses: number;
          /**
           * False when the fresh status check failed, so the counts above came
           * from login-time data, which carries no per-bus unlock information
           * at all. Quoting a count then would tell a team they are about to
           * spend a bus on one they may already own, so the prompt asks
           * without one.
           */
          readonly isStatusVerified: boolean;
      };

export const LOADING_STATE: BusUnlockState = { kind: UNLOCK_STATE_LOADING };
export const ALREADY_UNLOCKED_STATE: BusUnlockState = {
    kind: UNLOCK_STATE_ALREADY_UNLOCKED
};
export const UNLOCK_ON_SELECT_STATE: BusUnlockState = {
    kind: UNLOCK_STATE_UNLOCK_ON_SELECT
};
export const UNAVAILABLE_STATE: BusUnlockState = {
    kind: UNLOCK_STATE_UNAVAILABLE
};

/**
 * Decides how a bus selection should be unlocked.
 *
 * Unlocking is permanent and, on a bus-limited subscription, spends one of the
 * team's buses -- but the map has always unlocked silently the moment a bus is
 * selected, so a stray click can cost a customer a bus they never wanted. This
 * is where we decide that we owe them a confirmation first.
 *
 * We only confirm when every one of these holds, so nothing changes for the
 * customers who asked for the low-friction flow in the first place:
 *  - the bus isn't already unlocked, so revisits stay friction-free,
 *  - the user's role can actually unlock buses (admin or screener),
 *  - their team's subscription in the bus' region is bus-limited, and
 *  - that subscription still has a bus left. At zero the server refuses the
 *    unlock and the "No buses left in subscription" pane explains why, which is
 *    more useful than a dialog offering something we can't deliver.
 */
export const deriveBusUnlockState = ({
    canUnlock,
    isBusUnlocked,
    maybeSubscription,
    isStatusVerified = true
}: {
    canUnlock: boolean;
    isBusUnlocked: boolean;
    /** The team's subscription for the bus' region, if they have one. */
    maybeSubscription: Subscription | undefined;
    /**
     * Whether `isBusUnlocked` and the subscription's counts were read fresh.
     * Defaults to true; the caller passes false when it is falling back to
     * login-time data because the fresh status check failed.
     */
    isStatusVerified?: boolean;
}): BusUnlockState => {
    if (isBusUnlocked) {
        return ALREADY_UNLOCKED_STATE;
    }

    if (
        !canUnlock ||
        maybeSubscription === undefined ||
        maybeSubscription.unlimited
    ) {
        return UNLOCK_ON_SELECT_STATE;
    }

    const busesRemaining =
        getBusesRemainingOnLimitedSubscription(maybeSubscription);
    if (busesRemaining === 0) {
        return UNLOCK_ON_SELECT_STATE;
    }

    return {
        kind: UNLOCK_STATE_NEEDS_CONFIRMATION,
        busesRemaining,
        numBuses: maybeSubscription.numBuses,
        isStatusVerified
    };
};

/**
 * Why the server refused to unlock a bus, i.e. why `unlock_bus` returned null.
 * Mirrors the refusal branches of the `unlock_bus` Postgres function in
 * `nira-hasura`, so the pane can say what actually went wrong instead of always
 * blaming the bus count.
 */
/** Bus-limited subscription and this user's role isn't allowed to unlock. */
export const UNLOCK_FAILURE_ROLE_CANNOT_UNLOCK = "roleCannotUnlock";
/** Bus-limited subscription with every bus already spent. */
export const UNLOCK_FAILURE_OUT_OF_BUSES = "outOfBuses";
/** The refusal doesn't match any reason we can explain. */
export const UNLOCK_FAILURE_UNKNOWN = "unknown";

export type BusUnlockFailureReason =
    | typeof UNLOCK_FAILURE_ROLE_CANNOT_UNLOCK
    | typeof UNLOCK_FAILURE_OUT_OF_BUSES
    | typeof UNLOCK_FAILURE_UNKNOWN;

/**
 * Explains a refused unlock.
 *
 * The pane used to report every refusal as "No buses left in subscription",
 * which is wrong (and unactionable) for a reader whose team has plenty of buses
 * left. Walking the same branches `unlock_bus` does tells the user what to
 * actually do about it:
 *  - bus-limited and the role can't unlock -> ask an admin,
 *  - bus-limited otherwise -> the bus limit really is the reason,
 *  - anything else -> we have no honest explanation, so don't invent one.
 *
 * That last bucket covers the two refusals we can't attribute, both of which
 * should be unreachable:
 *  - No subscription in the bus' region. `unlock_bus` refuses this, but the user
 *    can't get far enough to trigger it: scope visibility is gated on holding a
 *    subscription in the region (the `user_scope_permissions` view), so without
 *    one the region's buses come back with no `capacity_costs`, which makes them
 *    unauthorized buses that never mount this pane at all.
 *  - Unlimited subscription. `unlock_bus` never refuses these -- `unlimited`
 *    short-circuits both the role and bus-limit checks -- so a refusal here
 *    means something we don't model (a missing user-id header is the only
 *    branch left).
 *
 * Deliberately does not look at the unlocked-bus *count*: the count available
 * to the pane is the one fetched at login, which undercounts anything the team
 * has unlocked since, so an admin who genuinely ran out mid-session would get
 * "we can't explain this" instead of the correct bus-limit message.
 */

export const deriveBusUnlockFailureReason = ({
    canUnlock,
    maybeSubscription
}: {
    canUnlock: boolean;
    /** The team's subscription for the bus' region, if they have one. */
    maybeSubscription: Subscription | undefined;
}): BusUnlockFailureReason => {
    if (maybeSubscription === undefined || maybeSubscription.unlimited) {
        return UNLOCK_FAILURE_UNKNOWN;
    }

    if (!canUnlock) {
        return UNLOCK_FAILURE_ROLE_CANNOT_UNLOCK;
    }

    return UNLOCK_FAILURE_OUT_OF_BUSES;
};
